<template>
  <div class="mywidgets">
    <h5 class="mywidgets-heading">My dashboard widgets</h5>
    <!-- Decision 8: this screen is open to every user, because every call it makes is about
         the signed-in user and none of them takes a user id. -->
    <p class="mywidgets-note">
      Switch a widget on to put it on your dashboard, and off to take it away. Nothing is
      deleted - a widget you switch off keeps its settings and comes back exactly as it was
      when you switch it on again.
    </p>

    <ProgressSpinner
      v-if="loading"
      style="width:25px; height:25px; width: 100%;"
      strokeWidth="5"
      fill="#EEEEEE"
      animationDuration=".5s"
    />
    <div v-else>
      <div
        v-for="entry in catalogue"
        :key="entry.widgetId"
        class="mywidgets-row p-grid p-ai-center"
      >
        <div class="p-col-3 p-sm-2 p-md-1">
          <InputSwitch
            class="mywidgets-switch"
            :value="entry.active"
            :disabled="isSaving(entry)"
            @input="setActive(entry, $event)"
          />
        </div>
        <div class="p-col-9 p-sm-10 p-md-11">
          <div class="mywidgets-title">{{ titleFor(entry) }}</div>
          <div class="mywidgets-state">{{ stateFor(entry) }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import UserService from "@/service/UserService";

// The whole catalogue with what this user holds folded in (contract §4), one switch each.
// Switching off is a deactivation - `PUT v1/myDashboardWidgets/{id}` with {"active": false} -
// so it is reversible by design and needs no confirmation. There is deliberately no confirm
// dialog here: a modal blocks the browser, and the undo is the same switch.

export default {
  created() {
    this.getCatalogue()
  },
  data: () => ({
    userService: new UserService(),
    catalogue: [],
    loading: false,
    // The widget ids with a call in flight, so a switch cannot be flipped twice over one call.
    saving: []
  }),
  methods: {
    titleFor(entry) {
      const title = entry && entry.title ? String(entry.title).trim() : ''
      return title || (entry && entry.widgetName) || ''
    },
    stateFor(entry) {
      return entry && entry.active
        ? 'On your dashboard'
        : 'Not on your dashboard - switch it on whenever you want it'
    },
    isSaving(entry) {
      return this.saving.indexOf(entry.widgetId) >= 0
    },
    getCatalogue() {
      this.loading = true
      this.userService
        .getMyDashboardWidgets()
        .then(data => {
          this.loading = false
          this.catalogue = Array.isArray(data) ? data : []
        })
        .catch(() => {
          this.loading = false
          this.$store.dispatch('addErrorMessage', {
            severity: 'error',
            content: 'Some data was not loaded from "myDashboardWidgets"'
          })
        })
    },
    // The switch moves at once; if the call fails it goes back, so it never shows a state the
    // server has not got.
    setActive(entry, active) {
      if (!entry || this.isSaving(entry)) return
      const previous = entry.active

      this.$set(entry, 'active', active)
      this.saving = this.saving.concat([entry.widgetId])

      this.userService
        .setMyDashboardWidget(entry.widgetId, active)
        .then(updated => {
          this.saving = this.saving.filter(id => id !== entry.widgetId)
          if (updated && typeof updated === 'object') {
            Object.keys(updated).forEach(key => this.$set(entry, key, updated[key]))
          }
          // UserService.getUserWidgets serves the dashboard's widgets from this cache for the
          // whole session, so without emptying it the change would not show on the dashboard
          // until a page reload (W5c contract §5.2). Only on success: a failed call changed
          // nothing on the server, so the cache is still right.
          this.$store.dispatch('commons/userWidgets', null)
          // Emptying the cache is not enough when this list is open inside the dashboard's own
          // filter panel (W9 §2.1): the dashboard is already drawn, so it is told to fetch its
          // widgets again. On its own screen nothing is listening and this does nothing.
          this.$emit('changed', entry)
        })
        .catch(() => {
          this.saving = this.saving.filter(id => id !== entry.widgetId)
          this.$set(entry, 'active', previous)
          this.$store.dispatch('addErrorMessage', {
            severity: 'error',
            content: 'Your change was not saved to "myDashboardWidgets/{widgetId}"'
          })
        })
    }
  }
}
</script>

<style>
.mywidgets-row {
  border-bottom: 1px solid #e0e0e0;
  padding: 0.5rem 0;
}
.mywidgets-title {
  font-weight: 600;
}
.mywidgets-state {
  font-size: 0.85rem;
  opacity: 0.7;
}
</style>
